import React from 'react';
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  MessageSquare, 
  Clock, 
  Tag,
  X,
  ExternalLink,
  Bot
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Separator } from './ui/separator';

import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';

const CustomerProfileSidebar = ({ 
  conversation, 
  messages = [], 
  isOpen, 
  onClose 
}) => {
  if (!conversation) return null;

  // Calculate stats from messages
  const customerMessages = messages.filter(m => m.sender_type === 'customer' || m.query);
  const botMessages = messages.filter(m => m.sender_type === 'bot' || (!m.sender_type && m.response));
  const humanMessages = messages.filter(m => m.sender_type === 'human');

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta'
    });
  };

  const getChannelInfo = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return { icon: <FaWhatsapp className="w-3.5 h-3.5" />, name: 'WhatsApp', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
      case 'instagram':
        return { icon: <FaInstagram className="w-3.5 h-3.5" />, name: 'Instagram', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' };
      case 'messenger':
        return { icon: <FaFacebookMessenger className="w-3.5 h-3.5" />, name: 'Messenger', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400' };
      case 'tiktok':
        return { icon: <span className="text-xs font-bold">T</span>, name: 'TikTok Shop', color: 'bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-300' };
      default:
        return { icon: <MessageSquare className="w-3.5 h-3.5" />, name: 'Website', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
    }
  };

  const channelInfo = getChannelInfo(conversation.channel);

  return (
    <div className={`
      fixed lg:relative top-0 right-0 h-screen lg:h-full w-80 
      bg-white dark:bg-zinc-900 
      border-l border-slate-200 dark:border-zinc-800
      transform transition-transform duration-300 z-50
      ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0 lg:hidden'}
      overflow-y-auto
    `}>
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-zinc-900 p-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 dark:text-zinc-50">
          Profil Customer
        </h3>
        <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-4 space-y-6">
        {/* Avatar & Name */}
        <div className="text-center">
          {/* Profile Picture — use real pic for IG/Messenger, avatar for WA */}
          {(conversation.customer_avatar || conversation.customer_profile_pic) ? (
            <img 
              src={conversation.customer_avatar || conversation.customer_profile_pic}
              alt={conversation.customer_name || 'Customer'}
              className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-slate-200 dark:border-zinc-700"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 items-center justify-center text-white text-3xl font-bold mx-auto mb-3 ${(conversation.customer_avatar || conversation.customer_profile_pic) ? 'hidden' : 'flex'}`}
          >
            {conversation.customer_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <h4 className="text-lg font-semibold text-slate-900 dark:text-zinc-50">
            {conversation.customer_name || 'Unknown Customer'}
          </h4>
          
          {/* IG username */}
          {conversation.channel === 'instagram' && conversation.customer_username && (
            <a 
              href={`https://instagram.com/${conversation.customer_username}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-pink-600 hover:underline dark:text-pink-400"
            >
              @{conversation.customer_username}
            </a>
          )}
          
          {/* Messenger profile link */}
          {conversation.channel === 'messenger' && (conversation.platform_sender_id || conversation.customer_id) && (
            <a 
              href={`https://facebook.com/${conversation.platform_sender_id || conversation.customer_id}`}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 hover:underline flex items-center justify-center gap-1 mt-1 dark:text-indigo-400"
            >
              <ExternalLink className="w-3 h-3" /> Lihat Profil Facebook
            </a>
          )}
          
          <div className="mt-2">
            <Badge className={`${channelInfo.color} gap-1.5`}>
              {channelInfo.icon} {channelInfo.name}
            </Badge>
          </div>
        </div>

        <Separator />

        {/* Contact Info */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            Informasi Kontak
          </h5>
          
          <div className="flex items-center gap-3 text-sm">
            <Phone className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
            <span className="text-slate-700 dark:text-zinc-300">
              {conversation.customer_phone || 'N/A'}
            </span>
          </div>

          {conversation.customer_email && (
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span className="text-slate-700 dark:text-zinc-300">
                {conversation.customer_email}
              </span>
            </div>
          )}
        </div>

        <Separator />

        {/* Conversation Stats */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            Statistik Percakapan
          </h5>
          
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-3 text-center">
              <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                {conversation.message_count || messages.length}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Total Pesan</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {botMessages.length}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Respon Bot</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {humanMessages.length}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Respon Manusia</p>
            </Card>
            <Card className="p-3 text-center">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {customerMessages.length}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Pesan Customer</p>
            </Card>
          </div>
        </div>

        <Separator />

        {/* Timeline */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            Timeline
          </h5>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span className="text-slate-500 dark:text-zinc-400">Dibuat:</span>
              <span className="text-slate-700 dark:text-zinc-300">
                {formatDate(conversation.created_at)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500 dark:text-zinc-400" />
              <span className="text-slate-500 dark:text-zinc-400">Aktivitas Terakhir:</span>
              <span className="text-slate-700 dark:text-zinc-300">
                {formatDate(conversation.last_message_at)}
              </span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Status */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
            Status
          </h5>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-zinc-400">Status:</span>
              <Badge variant={
                conversation.status === 'resolved' ? 'default' :
                conversation.status === 'urgent' ? 'destructive' :
                'secondary'
              }>
                {conversation.status || 'open'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-zinc-400">Ditangani:</span>
              <Badge variant={conversation.handled_by === 'bot' ? 'secondary' : 'default'}>
                {conversation.handled_by === 'bot' ? <><Bot className="w-3 h-3 inline mr-1" />Bot</> : <><User className="w-3 h-3 inline mr-1" />Manusia</>}
              </Badge>
            </div>
          </div>
        </div>

        {/* Tags */}
        {conversation.tags && conversation.tags.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </h5>
              <div className="flex flex-wrap gap-2">
                {conversation.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Notes */}
        {conversation.notes && conversation.notes.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wider">
                Catatan
              </h5>
              <div className="space-y-2">
                {conversation.notes.map((note, idx) => (
                  <div key={idx} className="p-2 bg-slate-50 dark:bg-zinc-800 rounded text-sm text-slate-700 dark:text-zinc-300">
                    {note}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerProfileSidebar;
